import React from "react";
import TooltipArrow from "../tooltips/TooltipArrow";
import {
  darkenHexColor,
  getWorkerRoleColorClass,
  parseHexFromTailwindBgClass,
} from "../../../utils/business/worker";
import { getUserFullName, getUserInitials } from "../../../utils/business/user";

const DEFAULT_BADGE_BG_CLASS = "bg-gray-300";
const DEFAULT_BADGE_BG_HEX = "#d1d5db";

/**
 * WorkerInitials component renders a styled div containing worker initials.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.worker - The worker object containing first and last name.
 * @param {string} props.worker.first - The first name of the worker.
 * @param {string} props.worker.last - The last name of the worker.
 * @param {string} [props.className] - Additional class names for styling.
 * @param {boolean} [props.tooltip] - Whether to show a tooltip with the full name on hover.
 * @param {boolean} [props.colorByRole] - Apply role-based background color.
 * @param {number} [props.darken] - 0–1 amount to darken the badge background (0 = unchanged).
 * @param {boolean} [props.showName] - Show full name beside the badge.
 * @param {string} [props.append] - Prefix before the full name when `showName` is true.
 * @param {Object} [props.style] - Inline styles merged onto the badge.
 * @returns {JSX.Element}
 *
 * @example
 * <WorkerInitials worker={{ first: 'John', last: 'Doe' }} className="custom-class" tooltip={true} />
 */
const WorkerInitials = ({
  worker,
  className,
  tooltip = false,
  colorByRole = false,
  darken = 0,
  showName = false,
  append = "",
  style = {},
}) => {
  const initials = getUserInitials(worker, {
    fallback: "?",
    useEmailFallback: false,
  });
  const fullName = getUserFullName(worker) || "—";

  const roleColorClass = colorByRole ? getWorkerRoleColorClass(worker) : "";
  const baseBgClass = roleColorClass || DEFAULT_BADGE_BG_CLASS;
  const darkenAmount = Math.min(1, Math.max(0, Number(darken) || 0));
  const useDarkenedBg = darkenAmount > 0;
  const baseHex =
    parseHexFromTailwindBgClass(baseBgClass) ?? DEFAULT_BADGE_BG_HEX;
  const badgeBackgroundStyle = useDarkenedBg
    ? { backgroundColor: darkenHexColor(baseHex, 1 - darkenAmount) }
    : {};

  const isBold = worker?.worker_type?.some?.((type) => type.type === "Foreman");

  return (
    <>
      <div
        className={`rounded-3xl w-[22px] h-[22px] p-1 mr-[2px] flex items-center justify-center ${tooltip ? "relative group cursor-pointer" : ""} ${className} ${useDarkenedBg ? "" : baseBgClass} ${isBold ? "font-bold" : ""}`}
        style={{ ...badgeBackgroundStyle, ...style }}
      >
        <p className="text-2xs text-center text-black">
          {initials}
        </p>
        {tooltip && (
          <TooltipArrow
            text={fullName}
            position="above"
            className="text-white bg-gray-900"
            usePortal
          />
        )}
      </div>
      {showName && (
        <span className="min-w-0 truncate pr-1">{append ? `${append} ` : ""}{fullName}</span>
      )}
    </>
  );
};

export default WorkerInitials;
